import React from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";

interface Question {
  name: string;
  question: string;
  options: string[];
}

interface FormData {
  [key: string]: string;
}

// Banking Assessment Questions Data
const questionsData: Question[] = [
  {
    name: "question1",
    question:
      "1. What is the safest and fastest way to receive your MBSYEP paycheck?",
    options: [
      "Pick it up at the DOES office",
      "Have a friend collect it for you",
      "Use direct deposit to your bank account",
      "Wait for it to be mailed",
    ],
  },
  {
    name: "question2",
    question: "2. What is a checking account typically used for?",
    options: [
      "Storing money for retirement",
      "Investing in stocks",
      "Paying bills and daily expenses",
      "Getting a loan",
    ],
  },
  {
    name: "question3",
    question:
      "3. What happens if you spend more money than you have in your checking account?",
    options: [
      "The bank gives you extra money automatically",
      "Nothing happens",
      "You earn interest",
      "You may be charged an overdraft fee",
    ],
  },
  {
    name: "question4",
    question: "4. What is a benefit of using a debit card instead of cash?",
    options: [
      "It's unlimited and never runs out",
      "It's secure, trackable, and linked to your bank account",
      "It builds your credit",
      "It earns airline points",
    ],
  },
  {
    name: "question5",
    question: "5. What information do you need to set up direct deposit?",
    options: [
      "Your school ID and social security number",
      "A parent's phone number",
      "Bank account number and routing number",
      "A voided paycheck",
    ],
  },
  {
    name: "question6",
    question: "6. What should you do if your debit card is lost or stolen?",
    options: [
      "Tell your friends",
      "Wait to see if it turns up",
      "Report it to your bank immediately",
      "Go to an ATM and cancel it",
    ],
  },
  {
    name: "question7",
    question:
      "7. What is the difference between a savings account and a checking account?",
    options: [
      "They are the same",
      "Savings earns less interest",
      "Savings is for saving money long-term; checking is for spending",
      "You can't use a debit card with either",
    ],
  },
  {
    name: "question8",
    question:
      "8. If you're under 18, what kind of account might you need to open at a bank?",
    options: [
      "Business account",
      "Premium account",
      "Joint account with a parent or guardian",
      "Offshore account",
    ],
  },
  {
    name: "question9",
    question: "9. How often should you check your bank account balance?",
    options: [
      "Once a year",
      "Never",
      "Every few months",
      "Regularly to track spending and avoid overdrafts",
    ],
  },
  {
    name: "question10",
    question: "10. What is the purpose of the MBSYEP Banking Quiz?",
    options: [
      "To test math skills",
      "To open a bank account automatically",
      "To make sure youth understand how to get paid and use banking tools responsibly",
      "To delay program entry",
    ],
  },
];

// Create validation schema dynamically
const schemaShape = questionsData.reduce((acc, { name }) => {
  acc[name] = yup.string().required("This field is required");
  return acc;
}, {} as Record<string, yup.StringSchema>);

const schema = yup.object().shape(schemaShape);

// Create default values dynamically
const defaultValues = questionsData.reduce((acc, { name }) => {
  acc[name] = "";
  return acc;
}, {} as FormData);

export default function Test() {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    console.log("Banking Assessment Results:", data);
    // Add your submission logic here
    // For example: API call to submit test results
  };

  const handleReAttempt = () => {
    reset(defaultValues);
  };

  return (
    <div className="p-16 flex justify-center min-h-screen bg-gray-50">
      <Paper className="p-32 max-w-4xl w-full bg-white rounded-2xl shadow-lg">
        <Typography
          variant="h4"
          className="mb-24 text-center font-semibold text-gray-800"
        >
          MBSYEP Banking Assessment
        </Typography>

        <Typography variant="body1" className="mb-32 text-center text-gray-600">
          Please answer all questions to complete your banking assessment. This
          quiz ensures you understand how to receive your paycheck and use
          banking tools responsibly.
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-24">
          {questionsData.map(({ name, question, options }) => (
            <FormControl
              component="fieldset"
              key={name}
              error={!!errors[name]}
              fullWidth
              className="bg-gray-50 p-16 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors duration-200"
            >
              <FormLabel
                component="legend"
                className="font-semibold text-gray-800 mb-12"
              >
                {question}
              </FormLabel>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} className="ml-8">
                    {options.map((opt) => (
                      <FormControlLabel
                        key={opt}
                        value={opt}
                        control={
                          <Radio
                            sx={{
                              color: "#6b7280",
                              "&.Mui-checked": {
                                color: "#3b82f6",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography className="text-gray-700">
                            {opt}
                          </Typography>
                        }
                        className="mb-8"
                      />
                    ))}
                  </RadioGroup>
                )}
              />
              {errors[name] && (
                <Typography
                  color="error"
                  variant="caption"
                  className="mt-8 font-medium"
                >
                  {errors[name]?.message}
                </Typography>
              )}
            </FormControl>
          ))}

          <div className="flex justify-between items-center mt-32 pt-24 border-t border-gray-200">
            <Typography variant="body2" className="text-gray-600 italic">
              All questions must be answered to submit the assessment
            </Typography>

            <div className="flex gap-12">
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleReAttempt}
                className="px-24 py-12 font-semibold"
              >
                Reset Quiz
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isValid}
                className="px-24 py-12 font-semibold"
              >
                Submit Banking Assessment
              </Button>
            </div>
          </div>
        </form>
      </Paper>
    </div>
  );
}
